// src/NotificationContext.js
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import React, { useMemo, useEffect, createContext } from 'react';

import { useSnackbar, SnackbarCloseButton } from 'src/components/snackbar';
import { NotificationActionButton, notificationActionTarget } from 'src/components/notification-action';

import socket from './socket';
import { useAuthContext } from './auth/hooks';
import { getUsage } from './redux/slices/plan';
import { getListings } from './redux/slices/listing';

export const NotificationContext = createContext();

// A connection the server refused (sign-in not ready yet, e.g. a brand-new seller) is tried again after this long.
const RECONNECT_AFTER_REFUSAL_MS = 5000;

export const NotificationProvider = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const dispatch = useDispatch();
  const userId = user?.id;

  // Connected while a seller is signed in; the server admits the socket only with their sign-in.
  useEffect(() => {
    if (!userId) return undefined;

    let retry = null;
    const reconnectIfRefused = () => {
      // An active socket reconnects by itself; one the server refused stays down until connected again.
      if (socket.active) return;
      clearTimeout(retry);
      retry = setTimeout(() => socket.connect(), RECONNECT_AFTER_REFUSAL_MS);
    };

    socket.on('connect_error', reconnectIfRefused);
    socket.connect();

    return () => {
      clearTimeout(retry);
      socket.off('connect_error', reconnectIfRefused);
      socket.disconnect();
    };
  }, [userId]);

  useEffect(() => {
    // Listen for notifications from the server
    socket.on('notification', (data) => {
      const { message, type, userId: notifiedUserId, action } = data;

      // The server sends a seller only their own notifications; this also holds for one that hasn't been updated yet.
      if (!user || user.id !== notifiedUserId) return;

      // A notification with a button shows it on the pop-up, before the close button.
      const withButton = notificationActionTarget(action)
        ? {
          action: (snackbarId) => (
            <>
              <NotificationActionButton action={action} onClick={() => closeSnackbar(snackbarId)} sx={{ mr: 0.5 }} />
              <SnackbarCloseButton snackbarId={snackbarId} />
            </>
          ),
        }
        : {};
      const show = (text, options) => enqueueSnackbar(text, { ...options, ...withButton });

      // TWO-PHASE IMPORT: Listings imported immediately, enrichment in background
      // Handle import success (listings created)
      if (message.includes('imported!') && message.includes('syncing')) {
        // Import completed instantly - refresh table to show new listings
        dispatch(getListings());
        dispatch(getUsage());

        // Show success message
        show(message, {
          variant: type,
          autoHideDuration: 8000,
        });
      }
      // Handle enrichment notifications (background syncing)
      else if (message.includes('Syncing') && message.includes('in the background')) {
        // Show info about background enrichment
        show(message, {
          variant: 'info',
          autoHideDuration: 10000,
        });
      }
      // Handle enrichment progress updates (optional - can add if needed)
      else if (message.includes('enrichment') && message.includes('progress')) {
        // Refresh to show updated listing statuses
        dispatch(getListings());
        dispatch(getUsage());

        show(message, {
          variant: 'info',
          autoHideDuration: 5000,
        });
      }
      // Handle enrichment completion
      else if (message.includes('All listings synced') || message.includes('enrichment completed')) {
        // Final refresh to show synced listings without badges
        dispatch(getListings());
        dispatch(getUsage());

        show(message, {
          variant: 'success',
          autoHideDuration: 6000,
        });
      }
      // Handle enrichment errors
      else if (message.includes('Failed to sync listing')) {
        show(message, {
          variant: 'warning',
          autoHideDuration: 8000,
        });
      }
      // Regular notifications (import, delete, update, etc)
      else {
        // Refresh listings table for any listing-related notifications. A detected sale lands
        // here, and it can take the last marketplace post down — so the plan usage moves too.
        if (message.toLowerCase().includes('listing')) {
          dispatch(getListings());
          dispatch(getUsage());
        }

        show(message, {
          variant: type,
          autoHideDuration: type === 'error' ? 30000 : 5000,
          persist: false,
        });
      }
    });

    // Clean up the effect
    return () => {
      socket.off('notification');
    };
  }, [enqueueSnackbar, closeSnackbar, user, dispatch]);

  // Memoize the context value to prevent re-renders
  const contextValue = useMemo(() => ({}), []); // You can add values if needed

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
